import { Box, Button, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { DraftAgent, GitAgentOverlay, PreviewResult } from '../../api/admin.api';
import { useCreateSourceMutation, useUpdateSourceMutation } from '../../api/admin.api';
import { describeQueryError } from '../../api/describeQueryError.api';
import { ADMIN_REPOSITORIES_PATH } from '../../config';
import { HISTORY_VERSIONING, TAG_ADVICE } from '../../content/adminVersioning';
import { cardSurfaceSx, tokens } from '../../theme';
import { MethodPicker } from './MethodPicker';
import { isEditable, PROVENANCE_LABEL } from './provenance';

type Ready = Extract<PreviewResult, { state: 'ready' }>;

/** What the admin has typed for one Agent Skill. A key is present only once it has been touched. */
type Edits = { title?: string; summary?: string; methods?: string[]; include?: boolean };

const Origin = ({ from }: { from: keyof typeof PROVENANCE_LABEL }) => (
  <Typography
    component="span"
    variant="body2"
    sx={{ color: from === 'missing' ? 'error.main' : 'text.secondary', fontSize: 12, fontWeight: from === 'missing' ? 600 : 400 }}
  >
    {PROVENANCE_LABEL[from]}
  </Typography>
);

const ReadOnlyField = ({ label, value, from }: { label: string; value: string; from: keyof typeof PROVENANCE_LABEL }) => (
  <Box>
    <Typography variant="overline" component="p" sx={{ color: 'text.secondary', letterSpacing: '1px' }}>
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
    <Origin from={from} />
  </Box>
);

const AgentDraft = ({ agent, edits, onEdit }: { agent: DraftAgent; edits: Edits; onEdit: (changes: Edits) => void }) => {
  const summaryMissing = agent.summary.from === 'missing' && (edits.summary ?? '').trim() === '';
  const included = agent.sibling ? (edits.include ?? agent.included) : true;

  return (
    <Box component="li" sx={{ ...cardSurfaceSx, padding: 5, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'baseline' }}>
        <Typography variant="h3" component="h3" sx={{ fontFamily: tokens.fontFamily.mono }}>
          {agent.name}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {agent.promptPath}
        </Typography>
      </Box>

      {agent.sibling && (
        <FormControlLabel
          control={
            <Checkbox
              checked={included}
              onChange={(event) => { onEdit({ include: event.target.checked }); }}
            />
          }
          label="Import this as a separate Agent Skill"
        />
      )}

      {agent.sibling && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          This file has its own description, so it may be a second Agent Skill in the same folder. A file the
          prompt only points at is not one. It is imported only if you tick this.
        </Typography>
      )}

      {agent.warnings.map((warning) => (
        <Typography key={warning} variant="body2" role="note" sx={{ color: 'text.secondary' }}>
          {warning}
        </Typography>
      ))}

      {agent.problems.map((problem) => (
        <Typography key={problem} variant="body2" role="alert" sx={{ color: 'error.main' }}>
          {problem}
        </Typography>
      ))}

      {included && (
        <>
          {isEditable(agent.title.from) ? (
            <Box>
              <TextField
                fullWidth
                label={`Title of ${agent.name}`}
                value={edits.title ?? agent.title.value ?? ''}
                onChange={(event) => { onEdit({ title: event.target.value }); }}
              />
              <Origin from={agent.title.from} />
            </Box>
          ) : (
            <ReadOnlyField label="Title" value={agent.title.value ?? ''} from={agent.title.from} />
          )}

          {isEditable(agent.summary.from) ? (
            <Box>
              <TextField
                fullWidth
                multiline
                minRows={2}
                required
                label={`Summary of ${agent.name}`}
                value={edits.summary ?? agent.summary.value ?? ''}
                onChange={(event) => { onEdit({ summary: event.target.value }); }}
                error={summaryMissing}
                helperText={summaryMissing ? 'A sentence for the library card. The file has no description to take it from.' : undefined}
              />
              <Origin from={agent.summary.from} />
            </Box>
          ) : (
            <ReadOnlyField label="Summary" value={agent.summary.value ?? ''} from={agent.summary.from} />
          )}

          {isEditable(agent.methods.from) ? (
            <Box>
              <MethodPicker
                label={`Methods for ${agent.name}`}
                value={edits.methods ?? agent.methods.value ?? []}
                onChange={(methods) => { onEdit({ methods }); }}
              />
              <Origin from={agent.methods.from} />
            </Box>
          ) : (
            <ReadOnlyField label="Methods" value={(agent.methods.value ?? []).join(', ')} from={agent.methods.from} />
          )}
        </>
      )}
    </Box>
  );
};

/** The overlay for what was typed: only touched fields, and nothing blank. */
const overlayOf = (edits: Record<string, Edits>): Record<string, GitAgentOverlay> =>
  Object.fromEntries(
    Object.entries(edits).flatMap(([name, entry]) => {
      const overlay: GitAgentOverlay = {
        ...((entry.title ?? '').trim() === '' ? {} : { title: (entry.title ?? '').trim() }),
        ...((entry.summary ?? '').trim() === '' ? {} : { summary: (entry.summary ?? '').trim() }),
        ...(entry.methods === undefined ? {} : { methods: entry.methods }),
        ...(entry.include === undefined ? {} : { include: entry.include }),
      };
      return Object.keys(overlay).length === 0 ? [] : [[name, overlay]];
    }),
  );

const ACCESS: Record<Ready['source']['access']['kind'], (label: string) => string> = {
  public: () => 'Read with no key, because it is public.',
  'github-app': (label) => `Read through the GitHub App "${label}".`,
  'github-fine-grained-pat': (label) => `Read with the token "${label}".`,
  'gitlab-token': (label) => `Read with the GitLab token "${label}".`,
};

/**
 * What a Check found, as a form. **Each field says where its value came from**, the
 * ones the repository does not hold are inputs, and Pull stays off until nothing that
 * would be imported has a problem.
 *
 * Keyed by the caller on the source, so a second Check starts a fresh draft rather
 * than carrying over what was typed against another repository.
 */
export const SourceDraft = ({ preview }: { preview: Ready }) => {
  const navigate = useNavigate();
  const [create, createState] = useCreateSourceMutation();
  const [update, updateState] = useUpdateSourceMutation();
  const [edits, setEdits] = useState<Record<string, Edits>>({});
  const [marketplace, setMarketplace] = useState('');

  const { source, agents } = preview;
  // The way in that Check found is the key the source is saved with. None means it was public.
  const credentialId = source.access.credentialId ?? '';
  const importable = agents.filter((agent) => (agent.sibling ? (edits[agent.name]?.include ?? agent.included) : true));
  const blocked =
    importable.length === 0 ||
    importable.some(
      (agent) =>
        agent.problems.length > 0 ||
        (agent.summary.from === 'missing' && (edits[agent.name]?.summary ?? '').trim() === ''),
    );
  const isSaving = createState.isLoading || updateState.isLoading;
  const error = createState.error ?? updateState.error;

  const submit = () => {
    const overlay = overlayOf(edits);
    const request = source.exists
      ? update({
          id: source.id,
          overlay,
          ...(credentialId === '' ? {} : { credentialId }),
          ...(marketplace.trim() === '' ? {} : { marketplace: marketplace.trim() }),
        })
      : create({
          url: source.url,
          overlay,
          ...(credentialId === '' ? {} : { credentialId }),
          ...(marketplace.trim() === '' ? {} : { marketplace: marketplace.trim() }),
        });

    void request.unwrap().then(() => void navigate(ADMIN_REPOSITORIES_PATH)).catch(() => undefined);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <Box sx={{ ...cardSurfaceSx, padding: 5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: tokens.fontFamily.mono }}>
          {source.id}
        </Typography>
        <Typography variant="body2">
          {source.private ? 'Private or internal' : 'Public'}. Read at <code>{source.readAt}</code>, on the{' '}
          {source.versioning === 'tags' ? 'release tags' : 'history'} of <code>{source.defaultBranch}</code>.
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {ACCESS[source.access.kind](source.access.label)}
        </Typography>
        {preview.releases.length > 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {preview.releases.length} release{preview.releases.length === 1 ? '' : 's'} will be imported, newest{' '}
            {preview.releases[0]?.version}.
          </Typography>
        )}
        {source.exists && (
          <Typography variant="body2" role="note">
            This repository has already been added. Saving changes what it is imported with and syncs it.
          </Typography>
        )}
      </Box>

      {source.versioning === 'history' && (
        <Typography variant="body2" role="note" sx={{ color: 'text.secondary' }}>
          {HISTORY_VERSIONING} {TAG_ADVICE}
        </Typography>
      )}

      {preview.problems.map((problem) => (
        <Typography key={problem} variant="body2" role="alert" sx={{ color: 'error.main' }}>
          {problem}
        </Typography>
      ))}

      <Box component="ul" sx={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {agents.map((agent) => (
          <AgentDraft
            key={agent.name}
            agent={agent}
            edits={edits[agent.name] ?? {}}
            onEdit={(changes) => { setEdits((current) => ({ ...current, [agent.name]: { ...current[agent.name], ...changes } })); }}
          />
        ))}
      </Box>

      {/* A marketplace is named `owner/repository`, which is a GitHub address, so a GitLab source has none. */}
      {source.host === 'github.com' && (
        <TextField
          label="Marketplace (optional)"
          value={marketplace}
          onChange={(event) => { setMarketplace(event.target.value); }}
          helperText="Where the plugin is listed, as owner/repository, such as and-digital/claude-marketplace. Used for the install instructions."
        />
      )}

      {error !== undefined && (
        <Typography role="alert" variant="body2" sx={{ color: 'error.main' }}>
          {describeQueryError(error)}
        </Typography>
      )}

      <Box>
        <Button variant="contained" disabled={blocked || isSaving} onClick={submit}>
          {source.exists ? 'Save and sync' : 'Pull into library'}
        </Button>
      </Box>
    </Box>
  );
};
